/**
 * Profit Report / Service Mix (BE-14d-1)
 *
 * One report per service over invoices finalised in the range: lines, quantity, revenue, cost, profit and margin,
 * with each service's share of the volume and of the revenue. Sorted by profit (Profit Report) or by volume
 * (Service Mix); the hub opens it with ?sort=PROFIT or ?sort=VOLUME.
 * Route: /reports/service-profit
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Grid, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ReportPage, { formatCurrency, formatDate } from '../../components/accounting/ReportPage';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { toYmd } from '../../utils/accounting/periodUtils';
import { serverMessage } from '../../utils/apiConvert';

const pct = (v) => (v === null || v === undefined ? '-' : `${Number(v).toFixed(2)}%`);

const ServiceProfit = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = searchParams.get('sort') === 'VOLUME' ? 'VOLUME' : 'PROFIT';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [from, setFrom] = useState(toYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [to, setTo] = useState(toYmd(new Date()));

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await financeReportsService.getServiceProfit({ from, to, sort }));
    } catch (err) {
      setReport(null);
      setError(serverMessage(err, 'Failed to load the report'));
    } finally {
      setLoading(false);
    }
  }, [from, to, sort]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const t = report?.totals;
  const columns = [
    { key: 'service', label: 'Service' },
    { key: 'lines', label: 'Lines', align: 'right', total: (x) => x.lines },
    { key: 'quantity', label: 'Quantity', align: 'right', total: (x) => x.quantity },
    { key: 'volumeShare', label: 'Volume share', align: 'right', render: (r) => pct(r.volumeShare), total: (x) => pct(x.volumeShare) },
    { key: 'revenue', label: 'Revenue', align: 'right', render: (r) => formatCurrency(r.revenue), total: (x) => formatCurrency(x.revenue) },
    { key: 'revenueShare', label: 'Revenue share', align: 'right', render: (r) => pct(r.revenueShare), total: (x) => pct(x.revenueShare) },
    { key: 'cost', label: 'Cost', align: 'right', render: (r) => formatCurrency(r.cost), total: (x) => formatCurrency(x.cost) },
    { key: 'profit', label: 'Profit', align: 'right', render: (r) => formatCurrency(r.profit), total: (x) => formatCurrency(x.profit) },
    { key: 'marginPercent', label: 'Margin', align: 'right', render: (r) => pct(r.marginPercent), total: (x) => pct(x.marginPercent) },
  ];

  const cards = t ? [
    { label: 'Services', value: (report.rows || []).length },
    { label: 'Quantity', value: t.quantity },
    { label: 'Revenue', value: formatCurrency(t.revenue), tone: 'good' },
    { label: 'Cost', value: formatCurrency(t.cost) },
    { label: 'Profit', value: formatCurrency(t.profit), tone: Number(t.profit) < 0 ? 'bad' : 'good' },
    { label: 'Margin', value: pct(t.marginPercent) },
  ] : [];

  return (
    <ReportPage
      title={sort === 'VOLUME' ? 'Service Mix' : 'Profit Report'}
      subtitle={`${sort === 'VOLUME' ? 'Services by volume' : 'Services by profit'} · invoices finalised ${formatDate(from)} – ${formatDate(to)}`}
      exportType="service-profit" exportParams={{ from, to, sort }} onError={setError}
      onRefresh={fetchData} error={error} onErrorClose={() => setError(null)}
      loading={loading} cards={cards}
      columns={columns} rows={report?.rows || []} rowKey={(r, i) => `${r.service}-${i}`}
      totals={t} totalsLabel="TOTAL"
      emptyMessage="No invoice was finalised in this range."
      filters={(
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Finalised from" type="date" value={from}
              onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Finalised to" type="date" value={to}
              onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ToggleButtonGroup exclusive size="small" value={sort}
              onChange={(e, v) => v && setSearchParams({ sort: v }, { replace: true })}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.8125rem', px: 2 } }}>
              <ToggleButton value="PROFIT">By profit</ToggleButton>
              <ToggleButton value="VOLUME">By volume</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      )}
    />
  );
};

export default ServiceProfit;
